import { Popup } from 'components/Popup'
import { BUTTON_TYPES, Button } from 'components/Button'
import { BTN_TXT } from 'constants/txt'
import { useFinancialsPopup } from 'features/Financials/Providers/FinancialsPopupProvider'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'
import { Typography } from 'components/Typography'
import { TPaymentData } from './types'
import { fetchMarketplaceARPaymentData, fetchPaymentData } from './api'
import { validatePaymentData } from './helpers'
import { formatAmount } from 'helper/common'
import { PAYMENT_TYPES } from 'features/Financials/types'
import { CognusARData } from 'features/Financials/CognusAR/CognusARTable/types'
import { TRebatesTableType } from 'features/Financials/Rebates/RebatesTable/types'

type TProps = {
  paymentType?: PAYMENT_TYPES.REBATES | PAYMENT_TYPES.MARKETPLACE_AR
}

export const ConfirmPaymentPopup = ({
  paymentType = PAYMENT_TYPES.REBATES
}: TProps) => {
  const isMarketplaceAR = paymentType === PAYMENT_TYPES.MARKETPLACE_AR
  const {
    initiateRebatesPaymentPopup,
    initiateMarketplaceARPaymentPopup,
    confirmRebatesPaymentPopup,
    confirmMarketplaceARPaymentPopup,
    unresolvedRebateQuarters
  } = useFinancialsPopup()
  const { actions, state } = isMarketplaceAR
    ? confirmMarketplaceARPaymentPopup
    : confirmRebatesPaymentPopup

  const { row } = state.payload
  const paymentLabel = isMarketplaceAR ? 'Marketplace AR' : 'Rebates'
  const payeeName = isMarketplaceAR
    ? (row as CognusARData)?.health_system
    : (row as TRebatesTableType)?.hospital_name

  const dispatch = useDispatch()
  const [paymentData, setPaymentData] = useState<TPaymentData | null>(null)
  const [loadedPaymentId, setLoadedPaymentId] = useState<string | null>(null)
  const isMarketplacePaymentLoading =
    isMarketplaceAR &&
    !!state.visible &&
    !!row?.uuid &&
    loadedPaymentId !== row.uuid

  useEffect(() => {
    if (!row?.uuid || !state.visible) return
    if (loadedPaymentId === row.uuid) return

    let isActive = true
    const requestPaymentData = isMarketplaceAR
      ? fetchMarketplaceARPaymentData
      : fetchPaymentData

    if (isMarketplaceAR) {
      dispatch(setLoading(true))
    }

    requestPaymentData(row.uuid)
      .then((data) => {
        if (!isActive) return
        if (
          isMarketplaceAR &&
          data.data.unresolved_rebate_quarters?.length > 0
        ) {
          confirmMarketplaceARPaymentPopup.actions.close()
          unresolvedRebateQuarters.actions.open({
            unresolvedRebateQuarters: data.data.unresolved_rebate_quarters
          })
          return
        }
        setPaymentData(data.data)
        setLoadedPaymentId(row.uuid)
      })
      .finally(() => {
        if (!isActive || !isMarketplaceAR) return
        dispatch(setLoading(false))
      })

    return () => {
      isActive = false
      if (isMarketplaceAR) {
        dispatch(setLoading(false))
      }
    }
  }, [
    confirmMarketplaceARPaymentPopup.actions,
    dispatch,
    isMarketplaceAR,
    loadedPaymentId,
    row?.uuid,
    state.visible,
    unresolvedRebateQuarters.actions
  ])

  const onCancel = useCallback(() => {
    actions.close()
  }, [actions])

  const onConfirm = useCallback(() => {
    if (!paymentData) return
    actions.close()
    const isValid = validatePaymentData(paymentData)
    if (!isValid) return
    if (isMarketplaceAR) {
      initiateMarketplaceARPaymentPopup.actions.open({
        row: state.payload.row as CognusARData,
        paymentData
      })
      return
    } else {
      initiateRebatesPaymentPopup.actions.open({
        row: state.payload.row as TRebatesTableType,
        paymentData
      })
    }
  }, [
    actions,
    initiateMarketplaceARPaymentPopup.actions,
    initiateRebatesPaymentPopup.actions,
    isMarketplaceAR,
    paymentData,
    state.payload.row
  ])

  if (!row || isMarketplacePaymentLoading) return null

  return (
    <>
      <Popup
        {...state}
        zIndex={10000}
        footer={
          <>
            <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
              {BTN_TXT.CANCEL}
            </Button>
            <Button type={BUTTON_TYPES.PRIMARY} onClick={onConfirm} upperCase>
              {BTN_TXT.PROCEED}
            </Button>
          </>
        }
      >
        <Typography.Body1>
          {paymentLabel} of {formatAmount(row.total)} will be paid to{' '}
          {payeeName}. Do you want to proceed?
        </Typography.Body1>
      </Popup>
    </>
  )
}
